/**
 * Limits Processor
 *
 * A utility for processing and validating constraints on phrases or collections of phrases.
 * This can handle both single arrays and multiple arrays of phrases for wider validation.
 */

import { Phrase, PhraseType } from "../../api/phrasebook";
import { BrandBatchSettings, BrandBatchSettingsWithPhrases } from "../../pages/playground/types";
import { getGroupLabelByPhraseType } from "../../utils/content-groups";
import { stripFormattingTags } from "../../utils/phrase-formatting";

type ValidationResult = {
  phrasesCount: number;
  hasPhrasesConstraints: boolean;
  needsToReachMinimumPhrases: boolean;
  charactersCount: number;
  hasCharactersConstraints: boolean;
  needsToReachMinimumCharacters: boolean;
  exceededMaxCharacters: boolean;
  wordsCount: number;
  hasWordsConstraints: boolean;
  needsToReachMinimumWords: boolean;
  exceededMaxWords: boolean;
  exceededMaxPhrases: boolean;
  forbiddenWords: string[];
};

/**
 * Process a single array of phrases and check against limits
 *
 * @param phrases - Array of phrase objects with a phrase property
 * @param limits - Object containing validation constraints
 * @returns Object with validation results and counts
 */
export function processPhrasesValidation(
  phrases: Phrase[],
  limits?: BrandBatchSettingsWithPhrases,
  wordsToAvoid?: string[]
): ValidationResult {
  // Count non-empty phrases
  const phrasesCount = phrases.filter((phrase) => phrase.phrase !== "").length;

  // Calculate total characters across all phrases (excluding formatting tags)
  const charactersCount = phrases.reduce(
    (acc, phrase) => acc + (stripFormattingTags(phrase?.phrase || "").length || 0),
    0
  );

  // Calculate total words across all phrases (excluding formatting tags)
  const wordsCount = phrases.reduce(
    (acc, phrase) =>
      acc + (stripFormattingTags(phrase?.phrase || "").split(" ").filter(Boolean).length || 0),
    0
  );

  // Check if there are any character constraints
  const hasPhrasesConstraints =
    Number(limits?.minPhrases ?? 0) > 0 &&
    Number(limits?.maxPhrases ?? 0) < Infinity;
  const hasCharactersConstraints =
    Number(limits?.minCharacters ?? 0) > 0 &&
    Number(limits?.maxCharacters ?? 0) < Infinity;
  const hasWordsConstraints =
    Number(limits?.minWords ?? 0) > 0 &&
    Number(limits?.maxWords ?? 0) < Infinity;

  // Validation checks
  const needsToReachMinimumPhrases =
    hasPhrasesConstraints && phrasesCount < Number(limits?.minPhrases ?? 0);
  const needsToReachMinimumCharacters =
    hasCharactersConstraints &&
    charactersCount < Number(limits?.minCharacters ?? 0);
  const needsToReachMinimumWords =
    hasWordsConstraints && wordsCount < Number(limits?.minWords ?? 0);

  const exceededMaxPhrases =
    hasPhrasesConstraints && phrasesCount > Number(limits?.maxPhrases ?? 0);
  const exceededMaxCharacters =
    hasCharactersConstraints &&
    charactersCount > Number(limits?.maxCharacters ?? 0);
  const exceededMaxWords =
    hasWordsConstraints && wordsCount > Number(limits?.maxWords ?? 0);

  const forbiddenWords: string[] = [];

  phrases.forEach((phrase) => {
    if (phrase.phrase) {
      const phraseText = stripFormattingTags(phrase.phrase);
      
      // First check for multi-word phrases
      wordsToAvoid?.forEach((forbiddenPhrase) => {
        // Use regex with word boundaries for exact matches (case-insensitive)
        const regex = new RegExp(`\\b${forbiddenPhrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        
        if (regex.test(phraseText) && !forbiddenWords.includes(forbiddenPhrase)) {
          forbiddenWords.push(forbiddenPhrase);
        }
      });
      
      // For backwards compatibility, also check individual words
      // This is helpful for exact matches of single words with word boundaries
      const words = phraseText
        .toLowerCase()
        .split(/[^a-zA-Z0-9àèéìòùÀÈÉÌÒÙ]/) // Split on any non-alphanumeric character (including Italian accents)
        .filter((word) => word.trim().length > 0);

      words.forEach((word) => {
        wordsToAvoid?.forEach((forbidden) => {
          // Only add single-word matches that aren't already found via phrase matching
          // Check if the forbidden word is a single word (no spaces)
          if (!forbidden.includes(' ') && 
              forbidden.toLowerCase() === word.toLowerCase() && 
              !forbiddenWords.includes(forbidden)) {
            forbiddenWords.push(forbidden);
          }
        });
      });
    }
  });

  return {
    phrasesCount,
    hasPhrasesConstraints,
    needsToReachMinimumPhrases,
    exceededMaxPhrases,
    charactersCount,
    hasCharactersConstraints,
    needsToReachMinimumCharacters,
    exceededMaxCharacters,
    wordsCount,
    hasWordsConstraints,
    needsToReachMinimumWords,
    exceededMaxWords,
    forbiddenWords,
  };
}

/**
 * Process multiple arrays of phrases and check against aggregate limits
 *
 * @param phrasesArrays - Array of phrase arrays to validate collectively
 * @param limits - Object containing validation constraints
 * @returns Object with validation results and counts
 */
export function processMultiplePhrasesLimits(
  phrasesArrays: Phrase[][],
  limits?: BrandBatchSettingsWithPhrases,
  wordsToAvoid?: string[]
): ValidationResult {
  // Flatten all phrases into a single array
  const allPhrases = phrasesArrays.flat();

  // Use the single array processor
  return processPhrasesValidation(allPhrases, limits, wordsToAvoid);
}

/**
 * Get a summary of validation issues for display
 *
 * @param validationResult - The result from processing phrases
 * @param limits - The limits used for validation
 * @param phraseType - Optional phrase type to include in validation messages
 * @returns Array of validation issue messages
 */
export function getValidationIssues(
  validationResult: ValidationResult,
  limits: BrandBatchSettingsWithPhrases,
  phraseType: PhraseType,
  batchSettings?: BrandBatchSettings
): string[] {
  const issues: string[] = [];

  // Helper to add phrase type context to messages if provided
  const label = getGroupLabelByPhraseType(batchSettings, phraseType);
  const typeContext = phraseType ? ` per ${label}` : "";

  // Phrases validation
  if (validationResult.needsToReachMinimumPhrases) {
    issues.push(
      `Minimum ${limits.minPhrases} ${
        limits.minPhrases === 1 ? "phrase" : "phrases"
      }${typeContext} (currently ${validationResult.phrasesCount})`
    );
  }

  if (validationResult.exceededMaxPhrases) {
    issues.push(
      `Exceeded maximum of ${limits.maxPhrases} ${
        limits.maxPhrases === 1 ? "phrase" : "phrases"
      }${typeContext} (currently ${validationResult.phrasesCount})`
    );
  }

  // Characters validation
  if (validationResult.needsToReachMinimumCharacters) {
    issues.push(
      `Minimum ${limits.minCharacters} ${
        limits.minCharacters === 1 ? "character" : "characters"
      }${typeContext} (currently ${validationResult.charactersCount})`
    );
  }

  if (validationResult.exceededMaxCharacters) {
    issues.push(
      `Exceeded maximum of ${limits.maxCharacters} ${
        limits.maxCharacters === 1 ? "character" : "characters"
      }${typeContext} (currently ${validationResult.charactersCount})`
    );
  }

  // Words validation
  if (validationResult.needsToReachMinimumWords) {
    issues.push(
      `Minimum ${limits.minWords} ${
        limits.minWords === 1 ? "word" : "words"
      }${typeContext} (currently ${validationResult.wordsCount})`
    );
  }

  if (validationResult.exceededMaxWords) {
    issues.push(
      `Exceeded maximum of ${limits.maxWords} ${
        limits.maxWords === 1 ? "word" : "words"
      }${typeContext} (currently ${validationResult.wordsCount})`
    );
  }

  if (validationResult.forbiddenWords.length > 0) {
    issues.push(
      `Found the following forbidden terms: "${validationResult.forbiddenWords.join(
        '", "'
      )}" in ${label}`
    );
  }

  return issues;
}
